import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAssessmentConfigOPXP } from '../base/types';
import {
  ECycleStatus,
  ETrainingStatus,
  ICompleteWorkoutPayload,
  ICreateAssessmentPayload,
  ICycleClientStatus,
  IInitCyclesPayload,
  IReplaceCurrentWorkoutPayload,
  IReplaceWorkoutPayload,
  ISessionClientStatus,
  IUpdateCycleStatsPayload,
  IUpdateStatsPayload,
  IWorkoutClientStatus,
} from './types';
import { updateCycles, updateSessions, updateWorkouts } from './utils';

export interface ICyclesState {
  isPremium: boolean;
  loading: boolean;
  saving: boolean;
  // cycle statistics
  currentCycleId: number;
  currentSessionId: number;
  cycleLength: number;
  finishedCycles: number;
  finishedSessions: number;
  numberWorkouts: number;
  // current cycle state
  cycleStatus: ECycleStatus;
  startTimestamp: number;
  finishedTimestamp: number;
  // cycles
  cycles: ICycleClientStatus[];
  // sessions
  sessions: ISessionClientStatus[];
  // workouts
  workouts: IWorkoutClientStatus[];
  firstTimestamp: number;
  lastTimestamp: number;
}

export const initialState: ICyclesState = {
  isPremium: false,
  loading: true,
  saving: false,
  currentCycleId: 0,
  currentSessionId: 0,
  cycleLength: 0,
  finishedCycles: 0,
  finishedSessions: 0,
  numberWorkouts: 0,
  cycleStatus: ECycleStatus.None,
  startTimestamp: 0,
  finishedTimestamp: 0,
  cycles: [],
  sessions: [],
  workouts: [],
  firstTimestamp: 0,
  lastTimestamp: 0,
};

export const statePropName = 'cycles';

const cyclesSlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    initCycles: {
      reducer(state, action: PayloadAction<IInitCyclesPayload>) {
        state.isPremium = action.payload.isPremium;
      },
      prepare(userId: number, isPremium: boolean) {
        return { payload: { userId, isPremium } };
      },
    },
    loadCycles(state) {
      state.loading = true;
    },
    cyclesReady(state) {
      state.loading = false;
    },
    updateStats: {
      reducer(state, action: PayloadAction<IUpdateStatsPayload>) {
        const {
          currentCycleId,
          currentSessionId,
          cycleLength,
          finishedCycles,
          finishedSessions,
          numberWorkouts,
          firstTimestamp,
          lastTimestamp,
        } = action.payload;
        state.currentCycleId = currentCycleId;
        state.currentSessionId = currentSessionId;
        state.cycleLength = cycleLength;
        state.finishedCycles = finishedCycles;
        state.finishedSessions = finishedSessions;
        state.numberWorkouts = numberWorkouts;
        state.firstTimestamp = firstTimestamp ?? 0;
        state.lastTimestamp = lastTimestamp ?? 0;
      },
      prepare(
        currentCycleId: number,
        currentSessionId: number,
        cycleLength: number,
        finishedCycles: number,
        finishedSessions: number,
        numberWorkouts: number,
        firstTimestamp?: number,
        lastTimestamp?: number
      ) {
        return {
          payload: {
            currentCycleId,
            currentSessionId,
            cycleLength,
            finishedCycles,
            finishedSessions,
            numberWorkouts,
            firstTimestamp,
            lastTimestamp,
          },
        };
      },
    },
    updateCycleStats: {
      reducer(state, action: PayloadAction<IUpdateCycleStatsPayload>) {
        state.startTimestamp = action.payload.startTimestamp;
        state.finishedTimestamp = action.payload.finishedTimestamp;
      },
      prepare(startTimestamp: number, finishedTimestamp: number) {
        return { payload: { startTimestamp, finishedTimestamp } };
      },
    },
    updateCycle(state, action: PayloadAction<ICycleClientStatus>) {
      const cycles = updateCycles(state.cycles, action.payload);
      state.cycles = cycles;
    },
    updateSession(state, action: PayloadAction<ISessionClientStatus>) {
      const sessions = updateSessions(state.sessions, action.payload);
      state.sessions = sessions;
    },
    updateWorkouts(state, action: PayloadAction<IWorkoutClientStatus[]>) {
      const workouts = updateWorkouts(state.workouts, action.payload);
      state.workouts = workouts;
    },
    updateCycleStatus(state, action: PayloadAction<ECycleStatus>) {
      state.cycleStatus = action.payload;
    },
    updateSessionId(state, action: PayloadAction<number>) {
      state.currentSessionId = action.payload;
    },
    createAssessment: {
      reducer(state, action: PayloadAction<ICreateAssessmentPayload>) {
        // No state change needed
      },
      prepare(config: IAssessmentConfigOPXP, addCFQ: boolean) {
        return { payload: { config, addCFQ } };
      },
    },
    rebuildAssessment(state, action: PayloadAction<IAssessmentConfigOPXP>) {
      // No state change needed
    },
    assessmentCreated(state) {
      // No state change needed
    },
    finishCycle(state) {
      // No state change needed
    },
    cycleFinished(state) {
      // No state change needed
    },
    saveCycle(state) {
      // No state change needed
    },
    cycleSaved(state) {
      // No state change needed
    },
    syncCycles(state) {
      // No state change needed
    },
    cloneCycle(state) {
      // No state change needed
    },
    cloneCycleFinished(state) {
      // No state change needed
    },
    cloneLastSession(state) {
      // No state change needed
    },
    cloneLastSessionFinished(state) {
      // No state change needed
    },
    completeWorkout: {
      reducer(state, action: PayloadAction<ICompleteWorkoutPayload>) {
        // No state change needed
      },
      prepare(workoutId: number, workoutIndex: number) {
        return { payload: { workoutId, newStatus: ETrainingStatus.Finished, workoutIndex } };
      },
    },
    skipWorkout: {
      reducer(state, action: PayloadAction<ICompleteWorkoutPayload>) {
        // No state change needed
      },
      prepare(workoutId: number, workoutIndex: number) {
        return { payload: { workoutId, newStatus: ETrainingStatus.Skipped, workoutIndex } };
      },
    },
    updateWorkoutStatusDone(state) {
      // No state change needed
    },
    replaceCurrentWorkout: {
      reducer(state, action: PayloadAction<IReplaceCurrentWorkoutPayload>) {
        // No state change needed
      },
      prepare(workoutId: number, workoutIndex?: number) {
        return { payload: { workoutId, workoutIndex } };
      },
    },
    createTraining(state, action: PayloadAction<number | undefined>) {
      state.saving = true;
    },
    trainingCreated(state) {
      state.saving = false;
    },
    createTrainingFailed(state) {
      state.saving = false;
    },
    replaceWorkout: {
      reducer(state, action: PayloadAction<IReplaceWorkoutPayload>) {
        // No state change needed
      },
      prepare(sourceId: number, targetId: number) {
        return { payload: { sourceId, targetId } };
      },
    },
    replaceWorkoutSuccess(state) {
      // No state change needed
    },
    replaceWorkoutFailed(state) {
      // No state change needed
    },
    finishCurrentSession(state) {
      // No state change needed
    },
    currentSessionFinished(state) {
      // No state change needed
    },
    cyclesClean(state) {
      // No state change needed
    },
    cleanDone(state) {
      // No state change needed
    },
    loadAssessment(state, action: PayloadAction<number>) {
      // No state change needed
    },
    loadAssessmentComplete(state, action: PayloadAction<number>) {
      // No state change needed
    },
  },
});

export const CyclesActions = cyclesSlice.actions;
export default cyclesSlice.reducer;
